import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { clearStudioDraft, peekStudioDraft } from '@/lib/ai-tools/resume';
import { track } from '@/lib/track';
import { m } from '@/paraglide/messages.js';

const POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

/**
 * Picks a studio up where Stripe left it. When the page opens with
 * `?credits=added` and a parked draft exists, it restores the form, waits
 * (briefly) for the purchased credits to show up on the balance, then starts
 * the render. If the credits don't land in time the form stays filled and the
 * visitor presses Generate themselves.
 */
export function useStudioResume<T>({
  slug,
  isAuthenticated,
  restore,
  refetchBalance,
  needed,
  start,
}: {
  slug: string;
  isAuthenticated: boolean;
  restore: (draft: T) => void;
  refetchBalance: () => Promise<number | undefined>;
  /** Credits the restored render costs. */
  needed: number;
  start: () => void;
}): { resuming: boolean } {
  const [resuming, setResuming] = useState(false);
  const latest = useRef({ restore, refetchBalance, needed, start });
  latest.current = { restore, refetchBalance, needed, start };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('credits') !== 'added') return;
    const draft = peekStudioDraft<T>(slug);
    if (!draft) return;
    latest.current.restore(draft);
    setResuming(true);
  }, [slug]);

  useEffect(() => {
    if (!resuming || !isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
        const balance = await latest.current.refetchBalance();
        if (cancelled) return;
        if (balance !== undefined && balance >= latest.current.needed) {
          clearStudioDraft(slug);
          setResuming(false);
          track('tool_resume_started', { tool: slug });
          latest.current.start();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (cancelled) return;
      }
      clearStudioDraft(slug);
      setResuming(false);
      track('tool_resume_manual', { tool: slug });
      toast.info(m['directory.ai_tools.studio.resume_manual']());
    })();
    return () => {
      cancelled = true;
    };
  }, [resuming, isAuthenticated, slug]);

  return { resuming };
}
